import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FileText, FolderOpen, Trash2, Loader2, CheckCircle2, AlertCircle, HardDrive, Users, RefreshCw, Folder } from "lucide-react";

import { brandStyle, brandSoftStyle } from "../../utils/branding";

export default function DocumentsTab({
  clientInfo,
  documents,
  subclients,
  selectedSubclient,
  setSelectedSubclient,
  fileInputRef,
  handleUpload,
  handleDroppedFiles,
  handleDeleteDoc,
  setAssignModal,
  formatSize,
  isUploading,
  maxStorage = 524288000,
  currentStorage = 0,
  selectedDocIds,
  setSelectedDocIds,
  onOpenBulkAccess,
  onBulkDelete,
  requirePolicyAcceptance = false,
  isPolicyAccepted = false,
  onPolicyAcceptedChange,
  portalSync = {},
  onRequestAgentSync,
  onCancelAgentSync,
  syncLoading = false,
  syncCancelLoading = false,
  localAgentEnabled = true,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (handleDroppedFiles && e.dataTransfer?.items?.length) {
      handleDroppedFiles(e.dataTransfer.items);
    }
  };

  const filteredDocs = selectedSubclient
    ? documents.filter((d) =>
        d.subclients?.some(
          (s) =>
            subclients.find((sc) => sc.id === selectedSubclient)?.name === s,
        ),
      )
    : documents;

  const selectableFiltered = useMemo(
    () => filteredDocs.filter((d) => d.status !== "processing"),
    [filteredDocs],
  );

  useEffect(() => {
    setSelectedDocIds((prev) =>
      prev.filter((id) => documents.some((d) => d.id === id)),
    );
  }, [documents, setSelectedDocIds]);

  useEffect(() => {
    setSelectedDocIds([]);
  }, [selectedSubclient, setSelectedDocIds]);

  const toggleSelectDoc = (id) => {
    setSelectedDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllFiltered = () => {
    setSelectedDocIds(selectableFiltered.map((d) => d.id));
  };

  const deselectAll = () => setSelectedDocIds([]);

  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((d) => selectedDocIds.includes(d.id));
  const someFilteredSelected = selectableFiltered.some((d) =>
    selectedDocIds.includes(d.id),
  );

  const headerSelectRef = useRef(null);
  useLayoutEffect(() => {
    const el = headerSelectRef.current;
    if (el)
      el.indeterminate = someFilteredSelected && !allFilteredSelected;
  }, [someFilteredSelected, allFilteredSelected]);

  return (
    <div
      className="space-y-6 animate-in fade-in duration-300"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-2">
        <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-blue-300 transition-colors">
          <div className="size-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-sm">
            <FileText size={28} />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
              Total Documents
            </div>
            <div className="text-3xl font-bold text-stone-900 mt-1">
              {documents.length}
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-brand-red/30 transition-colors">
          <div className="size-14 bg-brand-red/10 text-brand-red rounded-2xl flex items-center justify-center shadow-sm">
            <FolderOpen size={28} />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
              Sous-clients Actifs
            </div>
            <div className="text-3xl font-bold text-stone-900 mt-1">
              {subclients.length}
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-emerald-300 transition-colors">
          <div className="size-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-sm shrink-0">
            <HardDrive size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase truncate">
              {((currentStorage / maxStorage) * 100).toFixed(0)}% Utilisé
            </div>
            <div className="text-xl font-bold text-stone-900 mt-1 truncate">
              {formatSize(currentStorage)} <span className="text-sm font-normal text-stone-500">/ {formatSize(maxStorage)}</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-1.5 mt-2">
              <div 
                className={`h-1.5 rounded-full ${currentStorage / maxStorage > 0.9 ? 'bg-red-500' : 'bg-emerald-500'}`} 
                style={{ width: `${Math.min(100, Math.max(0, (currentStorage / maxStorage) * 100))}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled={isUploading}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (isUploading) return;
          if (requirePolicyAcceptance && !isPolicyAccepted) {
            alert(
              "Avant le premier upload, vous devez attester avoir lu la politique de confidentialite de la plateforme.",
            );
            return;
          }
          fileInputRef.current?.click();
        }}
        style={isDragging ? brandStyle(clientInfo) : brandSoftStyle(clientInfo)}
        className={`w-full text-left flex items-center gap-5 border-2 border-dashed rounded-2xl p-6 transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
          isDragging
            ? "shadow-lg"
            : "hover:brightness-95 hover:shadow-md cursor-pointer"
        }`}
      >
        <div
          className="size-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm"
          style={brandStyle(clientInfo)}
        >
          {isUploading ? (
            <Loader2 size={28} className="animate-spin" />
          ) : (
            <Folder size={28} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-lg">
            {isUploading
              ? "Transfert en cours..."
              : "Cliquez pour ajouter des fichiers"}
          </p>
          <p className="text-sm opacity-80 mt-0.5">
            ou glissez-déposez un dossier pour tout importer
          </p>
        </div>
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        <div className="p-6 border-b border-stone-100 flex flex-col gap-4 bg-stone-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2 text-stone-800">
            <FileText size={22} className="text-blue-500" /> Documents (
            {filteredDocs.length})
          </h2>
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {localAgentEnabled && (
                <button
                  type="button"
                  className={`px-4 py-2 rounded-xl text-sm font-medium shadow-sm transition-all flex items-center gap-2 border ${
                    syncLoading
                      ? "bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed"
                      : "bg-white hover:bg-stone-50 text-stone-800 border-stone-200 active:scale-[0.98]"
                  }`}
                  onClick={() => {
                    if (syncLoading || !onRequestAgentSync) return;
                    onRequestAgentSync();
                  }}
                  disabled={syncLoading}
                  title="Demande une synchronisation du dossier RAGUIA sur la machine où l’agent est installé"
                >
                  {syncLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  {syncLoading ? "Envoi..." : "Synchroniser (agent local)"}
                </button>
              )}
            </div>
            {localAgentEnabled &&
              (portalSync?.requested || portalSync?.last_sync_at) && (
              <p className="text-[11px] text-stone-500 max-w-md text-right leading-snug">
                {portalSync?.requested && (
                  <span className="text-amber-700 font-medium">
                    {portalSync?.last_sync_at
                      ? "Synchronisation demandée — en attente du prochain passage de l’agent sur un poste où il est installé. "
                      : "Demande enregistrée sur le serveur. Seul l’agent local (sur un PC avec le dossier RAGUIA) peut envoyer ces fichiers — le portail ne peut pas les récupérer à distance. "}
                    {onCancelAgentSync && (
                      <button
                        type="button"
                        className="underline text-amber-900 font-medium hover:text-amber-950 disabled:opacity-50"
                        onClick={() => {
                          if (syncCancelLoading) return;
                          onCancelAgentSync();
                        }}
                        disabled={syncCancelLoading}
                      >
                        {syncCancelLoading ? "Annulation…" : "Annuler la demande"}
                      </button>
                    )}
                  </span>
                )}
                {portalSync?.last_sync_at && (
                  <span className={portalSync?.requested ? "block mt-0.5" : ""} suppressHydrationWarning>
                    Dernière synchro agent :{" "}
                    {new Date(portalSync.last_sync_at).toLocaleString()}
                  </span>
                )}
                {portalSync?.last_error && (
                  <span className="block text-red-600 mt-0.5">
                    {portalSync.last_error}
                  </span>
                )}
              </p>
            )}
            {requirePolicyAcceptance && (
              <label className="mt-2 flex items-start gap-2 max-w-sm text-left">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                  checked={isPolicyAccepted}
                  onChange={(e) => onPolicyAcceptedChange?.(e.target.checked)}
                />
                <span className="text-xs text-stone-600 leading-relaxed">
                  J&apos;atteste avoir lu et accepte la{" "}
                  <a
                    href="/confidentialite-plateforme"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline"
                  >
                    politique de confidentialite de la plateforme
                  </a>
                  .
                </span>
              </label>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
          </div>

          {selectableFiltered.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 pt-1 border-t border-stone-100/80">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Sélection multiple
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"
                  onClick={selectAllFiltered}
                >
                  Tout sélectionner (cette vue)
                </button>
                <button
                  type="button"
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"
                  onClick={deselectAll}
                >
                  Tout désélectionner
                </button>
                {selectedDocIds.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-red text-white hover:bg-brand-red/90 flex items-center gap-1.5 shadow-sm"
                      onClick={onOpenBulkAccess}
                    >
                      <Users size={14} />
                      Accès pour {selectedDocIds.length} document
                      {selectedDocIds.length > 1 ? "s" : ""}
                    </button>
                    <button
                      type="button"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5 shadow-sm"
                      onClick={onBulkDelete}
                    >
                      <Trash2 size={14} />
                      Supprimer
                    </button>
                  </>
                )}
              </div>
              {selectedDocIds.length > 0 && (
                <span className="text-xs text-stone-500">
                  {selectedDocIds.length} sélectionné
                  {selectedDocIds.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>

        {subclients.length > 0 && (
          <div className="p-4 bg-stone-50 border-b border-stone-100 flex gap-2 overflow-x-auto custom-scrollbar">
            <button
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                !selectedSubclient
                  ? "text-white shadow-sm"
                  : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-100"
              }`}
              style={!selectedSubclient ? brandStyle(clientInfo) : undefined}
              onClick={() => setSelectedSubclient(null)}
            >
              Tous les documents
            </button>
            {subclients.map((s) => (
              <button
                key={s.id}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedSubclient === s.id
                    ? "text-white shadow-sm"
                    : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-100"
                }`}
                style={selectedSubclient === s.id ? brandStyle(clientInfo) : undefined}
                onClick={() => setSelectedSubclient(s.id)}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {selectedSubclient != null && subclients.length > 0 && (
          <p className="px-4 pb-3 text-xs text-amber-800 bg-amber-50/80 border-b border-amber-100">
            <strong className="text-amber-900">Import depuis ce sous-client :</strong> les
            nouveaux fichiers sont réservés à ce périmètre uniquement (pas d’accès général).
            Utilisez « Tous les documents » pour un import visible aussi en accès général.
          </p>
        )}

        {filteredDocs.length === 0 ? (
          <div key="empty" className="p-12 text-center text-stone-500">
            <div className="size-16 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={32} />
            </div>
            <p className="text-lg font-medium text-stone-700 mb-1">
              Aucun document trouvé
            </p>
            <p className="text-sm">
              Ajoutez des documents pour alimenter la base de connaissances.
            </p>
          </div>
        ) : (
          <div key="table" className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wider text-stone-500 font-semibold">
                  <th className="pl-6 pr-2 py-4 w-10">
                    {selectableFiltered.length > 0 ? (
                      <input
                        ref={headerSelectRef}
                        type="checkbox"
                        className="size-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                        checked={allFilteredSelected}
                        onChange={(e) => {
                          if (e.target.checked) selectAllFiltered();
                          else deselectAll();
                        }}
                        title="Sélectionner tous les documents de cette vue"
                      />
                    ) : null}
                  </th>
                  <th className="px-6 py-4">Fichier</th>
                  <th className="px-6 py-4">Statut</th>
                  <th className="px-6 py-4">Taille</th>
                  <th className="px-6 py-4">Sous-clients autorisés</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredDocs.map((d) => (
                  <tr
                    key={d.id}
                    className="hover:bg-stone-50/80 transition-colors group"
                  >
                    <td className="pl-6 pr-2 py-4 align-middle">
                      {d.status !== "processing" ? (
                        <input
                          type="checkbox"
                          className="size-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedDocIds.includes(d.id)}
                          onChange={() => toggleSelectDoc(d.id)}
                          aria-label={`Sélectionner ${d.filename}`}
                        />
                      ) : (
                        <span className="inline-block w-4" aria-hidden />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="size-10 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
                          <FileText size={20} />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-stone-800 block truncate">
                            {d.filename}
                          </span>
                          {d.source_display_path && (
                            <span
                              className="text-xs text-stone-500 font-mono truncate block mt-0.5"
                              title={d.source_display_path}
                            >
                              {d.source_display_path}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {d.status === "processing" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          <Loader2 size={12} className="animate-spin" /> En cours...
                        </span>
                      ) : d.status === "error" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 cursor-help" title="Erreur d'analyse">
                          <AlertCircle size={12} /> Erreur
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 size={12} /> Prêt
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-500 font-mono">
                      {formatSize(d.file_size)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {d.subclient_general && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-brand-red/10 text-brand-red border border-brand-red/20 italic">
                            Général
                          </span>
                        )}
                        {d.subclients?.map((s, i) => (
                          <span
                            key={`${d.id}-${s}-${i}`}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-stone-100 text-stone-600 border border-stone-200"
                          >
                            {s}
                          </span>
                        ))}
                        {!d.subclient_general &&
                          (!d.subclients || d.subclients.length === 0) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 border border-red-100">
                              Aucun accès
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="bg-white hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed text-stone-700 border border-stone-200 px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm transition-all flex items-center gap-1.5"
                          title="Gérer les accès aux sous-clients"
                          disabled={d.status === "processing"}
                          onClick={() => {
                            if (d.status === "processing") return;
                            const currentSids = subclients
                              .reduce((acc, sc) => {
                                if (d.subclients?.includes(sc.name)) acc.push(sc.id);
                                return acc;
                              }, []);
                            setAssignModal({
                              docId: d.id,
                              filename: d.filename,
                              selectedSid: currentSids,
                              generalAccess: d.subclient_general,
                            });
                          }}
                        >
                          <FolderOpen size={14} /> <span>Gérer l'accès</span>
                        </button>
                        <button
                          className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                          title="Supprimer"
                          disabled={d.status === "processing"}
                          onClick={() => {
                            if (d.status === "processing") return;
                            handleDeleteDoc(d);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
